import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { caseApi } from '../services/case.api';

interface NavbarProps {
  lang?: 'zh' | 'en';
}

const Navbar: React.FC<NavbarProps> = ({ lang = 'zh' }) => {
  const { t } = useTranslation('website');
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [hasCases, setHasCases] = useState(true);

  const sections = ['about', 'business', 'tech', 'cases', 'philosophy', 'vision', 'contact'];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: 0.3, rootMargin: '-80px 0px 0px 0px' }
    );

    sections.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    caseApi.getCases(lang).then(data => {
      setHasCases(data.cases.length > 0);
    }).catch(() => {});
  }, [lang]);

  const handleSmoothScroll = (e: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
    e.preventDefault();
    const el = document.getElementById(sectionId);
    if (el) {
      const offset = 80;
      const top = el.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
    setMobileOpen(false);
  };

  const navLinks = [
    { id: 'about', key: 'nav.about' },
    { id: 'business', key: 'nav.business' },
    { id: 'tech', key: 'nav.tech' },
    ...(hasCases ? [{ id: 'cases', key: 'nav.cases' }] : []),
    { id: 'philosophy', key: 'nav.philosophy' },
    { id: 'vision', key: 'nav.vision' },
    { id: 'contact', key: 'nav.contact' },
  ];

  return (
    <>
      <nav id="navbar" className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'scrolled' : ''}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <a href="#hero" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="flex items-center gap-3">
              <img src="/images/logo-128.png" alt="FancyPeter" className="w-8 h-8 rounded-xl object-contain" />
              <span className="text-white font-bold text-xl tracking-tight">FancyPeter</span>
            </a>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map(({ id, key }) => (
                <a
                  key={id}
                  href={`#${id}`}
                  onClick={(e) => handleSmoothScroll(e, id)}
                  className={`nav-link text-dark-300 hover:text-white transition-colors text-sm font-medium ${activeSection === id ? 'nav-active' : ''}`}
                >
                  {t(key)}
                </a>
              ))}
            </div>

            {/* Right side */}
            <div className="flex items-center gap-4">
              <Link
                to={lang === 'zh' ? '/en' : '/'}
                className="text-sm text-primary-400 hover:text-primary-300 font-medium transition-colors flex items-center gap-1"
              >
                <i data-lucide="languages" className="text-base" />
                {lang === 'zh' ? 'EN' : '中文'}
              </Link>
              <a
                href="#contact"
                onClick={(e) => handleSmoothScroll(e, 'contact')}
                className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 bg-primary-600 hover:bg-primary-500 text-white text-sm font-medium rounded-xl transition-all"
              >
                {t('nav.contactUs')} <i data-lucide="arrow-right" className="text-sm" />
              </a>
              <button
                id="menuBtn"
                className="md:hidden text-white"
                aria-label="Menu"
                onClick={() => setMobileOpen(true)}
              >
                <i data-lucide="menu" className="text-2xl" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div id="mobileMenu" className={`mobile-menu fixed inset-0 z-[60] bg-dark-950/98 backdrop-blur-xl ${mobileOpen ? 'open' : ''}`}>
        <button
          id="closeMenu"
          className="absolute top-6 right-6 text-white"
          onClick={() => setMobileOpen(false)}
        >
          <i data-lucide="x" className="text-2xl" />
        </button>
        {navLinks.map(({ id, key }) => (
          <a
            key={id}
            href={`#${id}`}
            onClick={(e) => handleSmoothScroll(e, id)}
            className="text-2xl text-dark-200 hover:text-white transition-colors mobile-link"
          >
            {t(key)}
          </a>
        ))}
        <Link
          to={lang === 'zh' ? '/en' : '/'}
          className="mt-4 text-sm text-primary-400 hover:text-primary-300 font-medium transition-colors flex items-center gap-1"
        >
          <i data-lucide="languages" className="text-base" />
          {lang === 'zh' ? 'English Version' : '中文版'}
        </Link>
      </div>
    </>
  );
};

export default Navbar;
